import { ChevronLeft, ChevronRight } from "lucide-react"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

interface PaginationProps {
  page: number          // 0-based
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  className?: string
}

export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className,
}: PaginationProps) {
  const start = total === 0 ? 0 : page * pageSize + 1
  const end = Math.min((page + 1) * pageSize, total)
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div
      className={cn(
        "flex flex-col gap-3 px-4 py-3 border-t border-line sm:flex-row sm:items-center sm:justify-between",
        className
      )}
    >
      <p className="text-caption text-subtle">
        Mostrando <strong className="text-foreground">{start}</strong>–
        <strong className="text-foreground">{end}</strong> de{" "}
        <strong className="text-foreground">{total}</strong>
      </p>

      <div className="flex items-center justify-between gap-3 sm:justify-end">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-caption text-subtle hidden sm:inline">Filas</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-9 w-20 text-body-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((opt) => (
                  <SelectItem key={opt} value={String(opt)}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="inline-flex h-10 w-10 sm:h-9 sm:w-9 items-center justify-center rounded-md border border-line bg-card text-foreground hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-body-sm text-neutral-700 dark:text-neutral-300 px-2 tabular-nums">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page + 1 >= totalPages}
            className="inline-flex h-10 w-10 sm:h-9 sm:w-9 items-center justify-center rounded-md border border-line bg-card text-foreground hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
